import { useRef, useState } from 'react'
import classnames from 'classnames'
import styles from './../../pages/songs/[id]/Components/_songTabs.module.scss'
import { t } from '../index'
import {
  DndContext,
  MouseSensor,
  useSensor,
  useSensors,
  closestCorners,
  TouchSensor
} from '@dnd-kit/core'
import { SortableContext, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ChordSection } from '../../__generated__/graphql'

export type TextState =
  | 'showChordsAndText'
  | 'showChordsOnly'
  | 'showLyricsOnly'
  | 'showNothing'
  | 'custom'

export const getListStyle = (isDraggingOver: boolean): string =>
  classnames(styles.tagList, {
    [styles.isDraggingOver]: isDraggingOver
  })

const reorder = (list: any[], startIndex: number, endIndex: number) => {
  if (startIndex > endIndex) {
    let result = list.slice(0, endIndex)
    result[endIndex] = list[startIndex]
    result = result.concat(list.slice(endIndex, startIndex))
    result = result.concat(list.slice(startIndex + 1, list.length))
    return result
  } else if (startIndex < endIndex) {
    let result = list.slice(0, startIndex)
    result = result.concat(list.slice(startIndex + 1, endIndex + 1))
    result[endIndex] = list[startIndex]
    result = result.concat(list.slice(endIndex + 1, list.length))
    return result
  } else {
    return list
  }
}

export interface ChordItem {
  acronym: string
  id: string
  index: number
  content: string
  name: string
  repeatCount: number
  isDragging?: boolean
  onClicked?: () => void
  hasLyrics?: boolean
}

export type ChordItemAction =
  | 'reorder'
  | 'repeat+'
  | 'repeat-'
  | 'duplicate'
  | 'remove'
  | 'changeIndividualSectionTextState'
  | 'changeGlobalSectionTextState'

export interface Change {
  action: ChordItemAction
  from?: number
  to?: number // only for reorder
  sectionStates?: TextState
  hasLyrics?: boolean
}

export const getDisplayKeyLabel = (itemShortName) => {
  if (itemShortName === '') {
    return ''
  }

  return itemShortName.match(/[a-z]+/i)[0]
}

// itemShortName = INT1, style key = INT
export const getItemStyle = (
  mainClass: string,
  isDragging: boolean,
  itemShortName: string
): string => {
  const key = getDisplayKeyLabel(itemShortName)

  return classnames(mainClass, {
    [styles.isDragging]: isDragging,
    [styles[key]]: true
  })
}

export const ChordItemComponent: React.FC<ChordItem> = (props: ChordItem) => (
  <div
    className={getItemStyle(
      styles.tagInsideContainer,
      props.isDragging,
      props.acronym
    )}
    onClick={props.onClicked}
  >
    <div
      className={classnames(
        styles.tag,
        styles[getDisplayKeyLabel(props.acronym)]
      )}
    >
      {props.acronym}
      {props.repeatCount > 1 && (
        <div className={styles.tagRepeatCount}>{props.repeatCount}X</div>
      )}
    </div>
  </div>
)

export const MAX_COUNT = 9

export const repeatCountNumber = (count) => {
  if (count < 1) {
    return 1
  } else if (count > MAX_COUNT) {
    return MAX_COUNT
  } else {
    return count
  }
}

const MAX_SECTIONS = 50 // max allowed sections, big number won't affect performance

export interface ChangeType {
  action: ChordItemAction
  from?: number
  to?: number
  sectionStates?: TextState
  hasLyrics?: boolean
}

interface TabsLogicAPI {
  hasChanges: () => boolean
  fakeChordItems: () => ChordItem[]
  chordItems: (chordCharts: ChordSection[]) => ChordItem[]
  undo: () => void
  backToDefault: () => void
  pushChange: (pushChangeProps: ChangeType) => void
  updateRepeat: (index: number) => (repeatCount: number) => void
  checkGlobalTextStatus: () => TextState
}

export enum SectionTextStates {
  showChordsAndText = 'showChordsAndText',
  showChordsOnly = 'showChordsOnly',
  showLyricsOnly = 'showLyricsOnly',
  showNothing = 'showNothing'
}

const SectionsTextTransitions = {
  [SectionTextStates.showChordsAndText]: SectionTextStates.showChordsOnly,
  [SectionTextStates.showChordsOnly]: SectionTextStates.showLyricsOnly,
  [SectionTextStates.showLyricsOnly]: SectionTextStates.showNothing,
  [SectionTextStates.showNothing]: SectionTextStates.showChordsAndText
}

const SectionsNonLyricsTransitions = {
  [SectionTextStates.showChordsAndText]: SectionTextStates.showNothing,
  [SectionTextStates.showChordsOnly]: SectionTextStates.showNothing,
  [SectionTextStates.showLyricsOnly]: SectionTextStates.showChordsOnly,
  [SectionTextStates.showNothing]: SectionTextStates.showChordsOnly
}

enum chordParser {
  space = '@',
  chordMain = '!',
  chordSuperscripted = '^',
  chordSlash = '/'
}

export const generateChordArray = (
  clearChords: Array<string>
): Array<string> => {
  const chordNeedsSeparation = (
    clearChords: Array<string>,
    index: number,
    chordStarted: number
  ) => {
    return Boolean(
      clearChords[index - 2] !== chordParser.chordSlash &&
        clearChords[index - 1] === chordParser.chordMain &&
        clearChords[index].search(/[A-Z1-9]/) === 0 &&
        chordStarted !== 0
    )
  }

  const chordIsStarted = (
    clearChords: Array<string>,
    index: number,
    chordStarted: number
  ) => {
    return Boolean(
      clearChords[index - 1] === chordParser.chordMain &&
        clearChords[index].search(/[A-Z1-9]/) === 0 &&
        chordStarted === 0
    )
  }

  const chordArray = []
  for (let i = 0; i < clearChords.length; i++) {
    if (clearChords[i] !== chordParser.space) {
      let currentChord = ''
      let chordStarted = 0
      while (clearChords[i] && clearChords[i] !== chordParser.space) {
        if (chordIsStarted(clearChords, i, chordStarted)) {
          chordStarted++
        } else if (chordNeedsSeparation(clearChords, i, chordStarted)) {
          chordArray.push(currentChord)
          currentChord = ''
        }
        // ignore chordMain('!') indicator for building chords
        if (clearChords[i] === chordParser.chordMain) {
          i++
          continue
        }
        currentChord += clearChords[i]
        i++
      }
      chordArray.push(currentChord)
    }
  }
  return chordArray
}

let globalTextStatus: TextState = SectionTextStates.showChordsAndText

// Initialize counts array with repeat values from chordCharts
const initializeCounts = (chordCharts: ChordSection[]): number[] => {
  const counts = new Array(MAX_SECTIONS).fill(1)
  if (chordCharts) {
    chordCharts.forEach(
      (chordSection, index) => (counts[index] = chordSection.repeat || 1)
    )
  }
  return counts
}

// Apply changes that affect counts/order/length
const applyCountsChange = (
  change: Change,
  order: number[],
  counts: number[],
  length: number
): [number[], number[], number] => {
  switch (change.action) {
    case 'reorder':
      order = reorder(order, change.from, change.to)
      counts = reorder(counts, change.from, change.to)
      break
    case 'repeat+':
      counts[change.from] += 1
      break
    case 'repeat-':
      counts[change.from] -= 1
      break
    case 'duplicate':
      for (let i = length; i > change.from; i--) {
        order[i] = order[i - 1]
        counts[i] = counts[i - 1]
      }
      length += 1
      break
    case 'remove':
      for (let i = change.from; i < length - 1; i++) {
        order[i] = order[i + 1]
        counts[i] = counts[i + 1]
      }
      length -= 1
      break
  }
  return [order, counts, length]
}

// Compute counts array by applying changes
const computeCounts = (
  chordCharts: ChordSection[],
  changes: Array<Change>
): number[] => {
  if (!chordCharts) {
    return new Array(MAX_SECTIONS).fill(1)
  }

  let length = chordCharts.length
  let order = [...Array(MAX_SECTIONS).keys()]
  let counts = initializeCounts(chordCharts)

  for (const change of changes) {
    // Only process changes that affect counts
    if (
      ['reorder', 'repeat+', 'repeat-', 'duplicate', 'remove'].includes(
        change.action
      )
    ) {
      ;[order, counts, length] = applyCountsChange(
        change,
        order,
        counts,
        length
      )
    }
  }

  return counts
}

export const chordItemsFromCharts = (
  chordCharts: ChordSection[],
  changes: Array<Change>
): {
  sectionsTextState: TextState
  acronym: string
  name: string
  index: number
  id: string
  content: string
  repeatCount: any
}[] => {
  if (!chordCharts) {
    return
  }
  let length = chordCharts.length
  let order = [...Array(MAX_SECTIONS).keys()]
  let sectionsTextState = new Array(MAX_SECTIONS).fill(
    SectionTextStates.showChordsAndText
  )
  let globalTextState: TextState = SectionTextStates.showChordsAndText
  let counts = initializeCounts(chordCharts)

  const applyChange = (
    change: Change,
    order: number[],
    counts: number[],
    sectionsTextState: Array<TextState>,
    length: number,
    globalTextState: TextState,
    globalTextStatus: TextState
  ): [number[], number[], Array<TextState>, number, TextState, TextState] => {
    if (
      ['reorder', 'repeat+', 'repeat-', 'duplicate', 'remove'].includes(
        change.action
      )
    ) {
      ;[order, counts, length] = applyCountsChange(
        change,
        order,
        counts,
        length
      )

      if (change.action === 'reorder') {
        sectionsTextState = reorder(sectionsTextState, change.from, change.to)
      }
    }

    switch (change.action) {
      case 'changeIndividualSectionTextState':
        const newState = change.hasLyrics
          ? SectionsTextTransitions[sectionsTextState[change.from]]
          : SectionsNonLyricsTransitions[sectionsTextState[change.from]]
        sectionsTextState[change.from] = newState
        globalTextStatus = 'custom'
        globalTextState = 'showChordsAndText'
        break
      case 'changeGlobalSectionTextState':
        if (change.sectionStates) {
          globalTextStatus = change.sectionStates
          globalTextState = change.sectionStates
          sectionsTextState = sectionsTextState.map(() => change.sectionStates)
        } else {
          sectionsTextState = sectionsTextState.map(
            () => SectionsTextTransitions[globalTextState]
          )
          globalTextStatus = SectionsTextTransitions[globalTextState]
          globalTextState = SectionsTextTransitions[globalTextState]
        }
        break
    }

    return [
      order,
      counts,
      sectionsTextState,
      length,
      globalTextState,
      globalTextStatus
    ]
  }

  const sectionHasLyrics = (content) => {
    return /<td class="chord-text">\w+<\/td>|<br \/>/.test(content)
  }

  const createSectionBlock = (
    content,
    textState: SectionTextStates,
    hasLyrics
  ) => {
    if (hasLyrics) {
      if (textState === SectionTextStates.showChordsAndText) return content
      if (textState === SectionTextStates.showNothing) return null
      if (textState === SectionTextStates.showLyricsOnly) {
        return content.replace(
          /(<td class="chord">.*?<\/td>|<td class="chord superscripted">.*?<\/td>)/g,
          ''
        )
      }
      return chordsOnly(content)
    } else {
      if (
        textState === SectionTextStates.showChordsAndText ||
        textState === SectionTextStates.showChordsOnly
      )
        return chordsOnly(content)
      return null
    }
  }

  const chordsOnly = (content) => {
    const allChords =
      content
        .replace(/<td class="chord-text">.*?<\/td>/g, '')
        .match(/<table>.+?<\/table>/g) || []

    const clearChords = allChords
      .map(
        (chordTable) =>
          chordTable
            .replace(
              /<table><tr>(<td class="chord">&nbsp;<\/td>)*<\/tr><\/table>/g,
              ''
            ) // Remove all empty tables without chords
            .replace(/<td class="chord">&nbsp;<\/td>/g, chordParser.space)
            .replace(/<td class="chord">/g, chordParser.chordMain)
            .replace(
              /<td class="chord superscripted">/g,
              chordParser.chordSuperscripted
            )
            .replace(/(<table><tr>|<\/td>|<\/tr><\/table>)/g, '') // Remove old tables html
      )
      .filter((table) => {
        return table.length !== 0
      })

    if (!clearChords.length) return ''

    const tableArray = clearChords.map((chordArr) => {
      return generateChordArray(chordArr)
    })

    const buildChord = (chord: string) => {
      if (chord === undefined) return ''
      const charArr = []
      chord.split('').forEach((char, index, chordArr) => {
        if (char === chordParser.chordSuperscripted) {
          // ignore this chordSuperscript('^') indicator
        } else if (
          char.search(/\w/) === 0 &&
          chordArr[index - 1] === chordParser.chordSuperscripted
        ) {
          charArr.push(
            `<span class="chord superscripted">${chordArr[index]}<\/span>`
          )
        } else {
          charArr.push(char)
        }
      })
      return charArr.join('')
    }

    const buildRow = (arr: []) => {
      return arr
        .map((char) => {
          return `
          <td class="chord" style="font-weight: bold">${buildChord(char)}</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
      `
        })
        .join('')
    }

    return `<div class="chord-root">
      ${tableArray
        .map((table) => {
          return `
              <table>
                <tr>
                  ${buildRow(table)}
                </tr>
              </table>`
        })
        .join('')}
      </div>`
  }

  for (const change of changes) {
    ;[
      order,
      counts,
      sectionsTextState,
      length,
      globalTextState,
      globalTextStatus
    ] = applyChange(
      change,
      order,
      counts,
      sectionsTextState,
      length,
      globalTextState,
      globalTextStatus
    )
  }

  return order.slice(0, length).map((orderIndex, index) => {
    const section = chordCharts[orderIndex]
    const hasLyrics = sectionHasLyrics(section.html)
    return {
      acronym: section.acronym,
      name: section.name,
      index: index,
      id: `${section.name}-${index}`,
      content: createSectionBlock(
        section.html,
        sectionsTextState[index],
        hasLyrics
      ),
      repeatCount: counts[index],
      sectionsTextState: sectionsTextState[index],
      hasLyrics: hasLyrics
    }
  })
}

export const initializeTabsLogic = (
  chordCharts: ChordSection[],
  changes: Array<Change>,
  setChanges: (change: Array<Change>) => void
): TabsLogicAPI => {
  const backToDefault = () => {
    setChanges([])
  }

  const undo = () => {
    setChanges(changes.slice(0, changes.length - 1))
  }

  const pushChange = ({
    action,
    from,
    to,
    sectionStates,
    hasLyrics
  }: ChangeType) => {
    const prev = changes[changes.length - 1]
    const newChanges = changes.slice(0)
    const undoRepeat = prev
      ? action === 'repeat-' && prev.action === 'repeat+' && from === prev.from
      : false
    const undoDuplicate = prev
      ? action === 'remove' &&
        prev.action === 'duplicate' &&
        Math.abs(from - prev.from) === 1
      : false

    if (undoRepeat || undoDuplicate) {
      newChanges.pop()
    } else {
      newChanges.push({ action, from, to, sectionStates, hasLyrics })
    }
    setChanges(newChanges)
  }

  const fakeChordItems = (): ChordItem[] => {
    return fakeChordCharts.map((section, index) => {
      return {
        acronym: section.acronym,
        name: section.name,
        index: index,
        id: `${section.name}-${index}`,
        content: section.html,
        repeatCount: 1
      }
    })
  }

  const hasChanges = (): boolean => changes.length > 0

  const updateRepeat = (index: number) => {
    const handler = (repeatCount) => {
      const counts = computeCounts(chordCharts, changes)
      const before = counts[index] || 1
      switch (repeatCount - before) {
        case +1:
          pushChange({ action: 'repeat+', from: index })
          break
        case -1:
          pushChange({ action: 'repeat-', from: index })
          break
      }
    }
    return handler
  }

  const checkGlobalTextStatus = (): TextState => globalTextStatus

  return {
    chordItems: () => chordItemsFromCharts(chordCharts, changes),
    fakeChordItems,
    pushChange,
    undo,
    backToDefault,
    hasChanges,
    updateRepeat,
    checkGlobalTextStatus
  }
}

export const SortableItemContainer = ({
  item,
  index,
  draggingIndex,
  pushChange,
  updateRepeat
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.id })
  const [isClicked, setIsClicked] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  const isDrag = draggingIndex === index

  const toggleHover = () => {
    setIsHovered(!isDrag && !isHovered)
  }
  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  }

  return (
    <div
      className={classnames(
        styles.tagContainer,
        styles[getDisplayKeyLabel(item.acronym)],
        {
          [styles.isHovered]: isDrag && isHovered,
          [styles.isClicked]: isClicked,
          [styles.isDragging]: isDrag
        }
      )}
    >
      <button
        onClick={() => pushChange({ action: 'duplicate', from: index })}
        className={classnames(styles.tagActionButton, {
          [styles.isActive]: isDrag
        })}
        onMouseUp={() => {
          setTimeout(() => {
            pushChange({ action: 'duplicate', from: index })
          }, 17)
        }}
      >
        {t('Duplicate')}
      </button>

      <button
        onClick={() => pushChange({ action: 'remove', from: index })}
        className={classnames(styles.tagActionButton, {
          [styles.isActive]: isDrag
        })}
        onMouseUp={() => {
          setTimeout(() => {
            pushChange({ action: 'remove', from: index })
          }, 17)
        }}
      >
        {t('Remove')}
      </button>

      {!isDrag && <span className={styles.tagRepeatText}>{t('Repeat')}</span>}

      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        onMouseEnter={toggleHover}
        onMouseLeave={toggleHover}
      >
        <ChordItemComponent
          {...item}
          index={index}
          isDragging={isDrag}
          onClicked={() => setIsClicked(!isClicked)}
        />
      </div>

      {!isDrag && (
        <div className={styles.tagRepeatControls}>
          <div
            onClick={() =>
              updateRepeat(item.index)(repeatCountNumber(item.repeatCount - 1))
            }
            className={item.repeatCount === 1 ? styles.isDisabled : ''}
          >
            <span>–</span>
          </div>
          <div
            onClick={() => {
              updateRepeat(item.index)(repeatCountNumber(item.repeatCount + 1))
            }}
            className={item.repeatCount === 9 ? styles.isDisabled : ''}
          >
            <span>+</span>
          </div>
        </div>
      )}
    </div>
  )
}

export const SortableList = ({ items, pushChange, updateRepeat }) => {
  const [draggingIndex, setDraggingIndex] = useState(null)

  const onDragStart = ({ active }) => {
    const index = items.findIndex((item) => item.id == active.id)
    setDraggingIndex(index)
  }
  const onDragEnd = ({ over, active }) => {
    setDraggingIndex(null)

    const oldIndex = items.findIndex((item) => item.id == active?.id)
    const newIndex = items.findIndex((item) => item.id == over?.id)

    if ((!newIndex && newIndex !== 0) || oldIndex === newIndex) {
      return
    }
    pushChange({ action: 'reorder', from: oldIndex, to: newIndex })
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10
      }
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5
      }
    })
  )
  return (
    <DndContext
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      sensors={sensors}
      collisionDetection={closestCorners}
      autoScroll={false}
    >
      <SortableContext items={items}>
        <div className={getListStyle(!!draggingIndex)}>
          {items.map((item, index) => (
            <SortableItemContainer
              item={item}
              index={index}
              draggingIndex={draggingIndex}
              key={`item-${item.id}`}
              pushChange={pushChange}
              updateRepeat={updateRepeat}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}

export const SongPart: React.FC<{
  item: ChordItem
  pushChange: (pushChangeProps: ChangeType) => void
  isLyrics?: boolean
}> = ({ item, pushChange, isLyrics }): JSX.Element => {
  const songPartRef = useRef<HTMLDivElement>()

  return (
    <div className={getSongPartClass(item.acronym)}>
      <div className={styles.chordChartPart}>
        <h4
          onClick={() => {
            if (!isLyrics) {
              pushChange({
                action: 'changeIndividualSectionTextState',
                from: item.index,
                hasLyrics: item.hasLyrics
              })
            }
          }}
          className={classnames(styles.chartName, {
            [styles.chartNameHover]: !isLyrics
          })}
        >
          {item.name}{' '}
          {item.repeatCount > 1 && (
            <span className={styles.repeatCount}>({item.repeatCount}X)</span>
          )}
        </h4>
        <div
          className={classnames(styles.songPart, {
            [styles.songPartLyrics]: isLyrics
          })}
          key={item.id}
          ref={songPartRef}
          dangerouslySetInnerHTML={{ __html: item.content }}
        />
      </div>
    </div>
  )
}

const getSongPartClass = (acronym: string): string => {
  if (Number(acronym[acronym.length - 1])) {
    const chordClass = acronym.slice(0, -1)
    return classnames(
      styles[chordClass] ? styles[chordClass] : styles.undefinedPart
    )
  } else {
    return classnames(styles[acronym] ? styles[acronym] : styles.undefinedPart)
  }
}

const fakeChordCharts = [
  {
    name: 'Intro',
    acronym: 'I',
    html: '<div class="chord-root"><table><tr><td class="chord">B&nbsp;</td><td class="chord">E&nbsp;</td><td class="chord">B/D#&nbsp;</td><td class="chord">F#sus&nbsp;</td></tr><tr></tr></table><table><tr><td class="chord">G#m&nbsp;</td><td class="chord">E&nbsp;</td><td class="chord">B/D#&nbsp;</td><td class="chord">F#sus&nbsp;</td><td class="chord">B&nbsp;</td></tr><tr></tr></table></div>'
  },
  {
    name: 'Verse',
    acronym: 'V',
    html: '<div class="chord-root"><table><tr><td class="chord">B</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td></tr><tr><td class="chord-text">T</td><td class="chord-text">h</td><td class="chord-text">e</td><td class="chord-text">&nbsp;</td><td class="chord-text">L</td><td class="chord-text">o</td><td class="chord-text">r</td><td class="chord-text">d</td><td class="chord-text">&nbsp;</td><td class="chord-text">b</td><td class="chord-text">l</td><td class="chord-text">e</td><td class="chord-text">s</td><td class="chord-text">s</td><td class="chord-text">&nbsp;</td><td class="chord-text">y</td><td class="chord-text">o</td><td class="chord-text">u</td></tr></table><table><tr><td class="chord">E</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td></tr><tr><td class="chord-text">a</td><td class="chord-text">n</td><td class="chord-text">d</td><td class="chord-text">&nbsp;</td><td class="chord-text">k</td><td class="chord-text">e</td><td class="chord-text">e</td><td class="chord-text">p</td><td class="chord-text">&nbsp;</td><td class="chord-text">y</td><td class="chord-text">o</td><td class="chord-text">u</td></tr></table><table><tr><td class="chord">B</td><td class="chord">/</td><td class="chord">D</td><td class="chord">#</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">F</td><td class="chord">#</td><td class="chord">s</td><td class="chord">u</td><td class="chord">s</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td></tr><tr><td class="chord-text">M</td><td class="chord-text">a</td><td class="chord-text">k</td><td class="chord-text">e</td><td class="chord-text">&nbsp;</td><td class="chord-text">H</td><td class="chord-text">i</td><td class="chord-text">s</td><td class="chord-text">&nbsp;</td><td class="chord-text">f</td><td class="chord-text">a</td><td class="chord-text">c</td><td class="chord-text">e</td><td class="chord-text">&nbsp;</td><td class="chord-text">s</td><td class="chord-text">h</td><td class="chord-text">i</td><td class="chord-text">n</td><td class="chord-text">e</td><td class="chord-text">&nbsp;</td><td class="chord-text">u</td><td class="chord-text">p</td><td class="chord-text">o</td><td class="chord-text">n</td><td class="chord-text">&nbsp;</td><td class="chord-text">y</td><td class="chord-text">o</td><td class="chord-text">u</td></tr></table><table><tr></tr><tr><td class="chord-text">And&nbsp;be&nbsp;gracious&nbsp;to&nbsp;you</td></tr></table><table><tr><td class="chord">G</td><td class="chord">#</td><td class="chord">m</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">E</td></tr><tr><td class="chord-text">T</td><td class="chord-text">h</td><td class="chord-text">e</td><td class="chord-text">&nbsp;</td><td class="chord-text">L</td><td class="chord-text">o</td><td class="chord-text">r</td><td class="chord-text">d</td><td class="chord-text">&nbsp;</td><td class="chord-text">t</td><td class="chord-text">u</td><td class="chord-text">r</td><td class="chord-text">n</td><td class="chord-text">&nbsp;</td><td class="chord-text">H</td><td class="chord-text">i</td><td class="chord-text">s</td><td class="chord-text"> </td></tr></table><table><tr></tr><tr><td class="chord-text">face&nbsp;toward&nbsp;you</td></tr></table><table><tr><td class="chord">B</td><td class="chord">/</td><td class="chord">D</td><td class="chord">#</td><td class="chord">&nbsp;</td><td class="chord">F</td><td class="chord">#</td><td class="chord">s</td><td class="chord">u</td><td class="chord">s</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">B</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td></tr><tr><td class="chord-text">A</td><td class="chord-text">n</td><td class="chord-text">d</td><td class="chord-text">&nbsp;</td><td class="chord-text">g</td><td class="chord-text">i</td><td class="chord-text">v</td><td class="chord-text">e</td><td class="chord-text">&nbsp;</td><td class="chord-text">y</td><td class="chord-text">o</td><td class="chord-text">u</td><td class="chord-text">&nbsp;</td><td class="chord-text">p</td><td class="chord-text">e</td><td class="chord-text">a</td><td class="chord-text">c</td><td class="chord-text">e</td></tr></table></div>'
  },
  {
    name: 'Vamp',
    acronym: 'VA',
    html: '<div class="chord-root"><table><tr><td class="chord">B&nbsp;</td><td class="chord">Bsus&nbsp;</td></tr><tr></tr></table></div>'
  },
  {
    name: 'Chorus',
    acronym: 'C',
    html: '<div class="chord-root"><table><tr><td class="chord">G</td><td class="chord">#</td><td class="chord">m</td><td class="chord">&nbsp;</td><td class="chord">E</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td></tr><tr><td class="chord-text">A</td><td class="chord-text">&nbsp;</td><td class="chord-text">-</td><td class="chord-text">&nbsp;</td><td class="chord-text">m</td><td class="chord-text">e</td><td class="chord-text">n</td></tr></table><table><tr><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">B</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td></tr><tr><td class="chord-text">A</td><td class="chord-text">&nbsp;</td><td class="chord-text">-</td><td class="chord-text">&nbsp;</td><td class="chord-text">m</td><td class="chord-text">e</td><td class="chord-text">n</td></tr></table><table><tr><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">F</td><td class="chord">#</td><td class="chord">&nbsp;</td></tr><tr><td class="chord-text">A</td><td class="chord-text">&nbsp;</td><td class="chord-text">-</td><td class="chord-text">&nbsp;</td><td class="chord-text">m</td><td class="chord-text">e</td><td class="chord-text">n</td></tr></table></div>'
  },
  {
    name: 'Interlude',
    acronym: 'INT',
    html: '<div class="chord-root"><table><tr><td class="chord">G</td><td class="chord">#</td><td class="chord">m</td><td class="chord">&nbsp;</td><td class="chord">E</td><td class="chord">&nbsp;</td><td class="chord">B</td><td class="chord">&nbsp;</td><td class="chord">F</td><td class="chord">#</td></tr><tr><td class="chord-text">&nbsp;</td><td class="chord-text"> </td><td class="chord-text">&nbsp;</td><td class="chord-text"> </td><td class="chord-text">&nbsp;</td><td class="chord-text"> </td><td class="chord-text">&nbsp;</td><td class="chord-text"> </td><td class="chord-text"> </td><td class="chord-text">&nbsp;</td></tr></table></div>'
  },
  {
    name: 'Bridge 1',
    acronym: 'B1',
    html: '<div class="chord-root"><table><tr><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">G</td><td class="chord">#</td><td class="chord">m</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td></tr><tr><td class="chord-text">M</td><td class="chord-text">a</td><td class="chord-text">y</td><td class="chord-text">&nbsp;</td><td class="chord-text">H</td><td class="chord-text">i</td><td class="chord-text">s</td><td class="chord-text">&nbsp;</td><td class="chord-text">f</td><td class="chord-text">a</td><td class="chord-text">v</td><td class="chord-text">o</td><td class="chord-text">r</td><td class="chord-text">&nbsp;</td><td class="chord-text">b</td><td class="chord-text">e</td><td class="chord-text">&nbsp;</td><td class="chord-text">u</td><td class="chord-text">p</td><td class="chord-text">o</td><td class="chord-text">n</td><td class="chord-text">&nbsp;</td><td class="chord-text">y</td><td class="chord-text">o</td><td class="chord-text">u</td></tr></table><table><tr><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">E</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td></tr><tr><td class="chord-text">A</td><td class="chord-text">n</td><td class="chord-text">d</td><td class="chord-text">&nbsp;</td><td class="chord-text">a</td><td class="chord-text">&nbsp;</td><td class="chord-text">t</td><td class="chord-text">h</td><td class="chord-text">o</td><td class="chord-text">u</td><td class="chord-text">s</td><td class="chord-text">a</td><td class="chord-text">n</td><td class="chord-text">d</td><td class="chord-text">&nbsp;</td><td class="chord-text">g</td><td class="chord-text">e</td><td class="chord-text">n</td><td class="chord-text">e</td><td class="chord-text">r</td><td class="chord-text">a</td><td class="chord-text">t</td><td class="chord-text">i</td><td class="chord-text">o</td><td class="chord-text">n</td><td class="chord-text">s</td></tr></table><table><tr><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">B</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td></tr><tr><td class="chord-text">A</td><td class="chord-text">n</td><td class="chord-text">d</td><td class="chord-text">&nbsp;</td><td class="chord-text">y</td><td class="chord-text">o</td><td class="chord-text">u</td><td class="chord-text">r</td><td class="chord-text">&nbsp;</td><td class="chord-text">f</td><td class="chord-text">a</td><td class="chord-text">m</td><td class="chord-text">i</td><td class="chord-text">l</td><td class="chord-text">y</td><td class="chord-text">&nbsp;</td><td class="chord-text">a</td><td class="chord-text">n</td><td class="chord-text">d</td><td class="chord-text">&nbsp;</td><td class="chord-text">y</td><td class="chord-text">o</td><td class="chord-text">u</td><td class="chord-text">r</td><td class="chord-text"> </td></tr></table><table><tr></tr><tr><td class="chord-text">children</td></tr></table><table><tr><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">F</td><td class="chord">#</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td><td class="chord">&nbsp;</td></tr><tr><td class="chord-text">A</td><td class="chord-text">n</td><td class="chord-text">d</td><td class="chord-text">&nbsp;</td><td class="chord-text">t</td><td class="chord-text">h</td><td class="chord-text">e</td><td class="chord-text">i</td><td class="chord-text">r</td><td class="chord-text">&nbsp;</td><td class="chord-text">c</td><td class="chord-text">h</td><td class="chord-text">i</td><td class="chord-text">l</td><td class="chord-text">d</td><td class="chord-text">r</td><td class="chord-text">e</td><td class="chord-text">n</td><td class="chord-text">&nbsp;</td><td class="chord-text">a</td><td class="chord-text">n</td><td class="chord-text">d</td><td class="chord-text">&nbsp;</td><td class="chord-text">t</td><td class="chord-text">h</td><td class="chord-text">e</td><td class="chord-text">i</td><td class="chord-text">r</td><td class="chord-text"> </td></tr></table><table><tr></tr><tr><td class="chord-text">children</td></tr></table></div>'
  }
]
